import React, { forwardRef, useEffect, useRef } from 'react';

import PropTypes from 'prop-types';
import { FaChevronRight } from 'react-icons/fa';
import styled from 'styled-components';

import {
  FormattedMessage,
  FormattedNumber,
} from '~/techstyle-shared/react-intl';

import useSelectedFacetsToApply from '../../hooks/useSelectedFacetsToApply';
import useUnifiedProductGridData from '../../hooks/useUnifiedProductGridData';

const FilterButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  border: none;
  gap: 2px;
  padding-left: 0;
  padding-right: 0;
  background: transparent;
  position: relative;
  font-family: inherit;
  cursor: pointer;
  ${({ theme }) => theme.gridFilterStyles.filterButtonMobileStyle};
`;

const MobileGridFiltersButton = forwardRef(
  ({ isOpen, setIsOpen = () => {} }, ref) => {
    const buttonRef = useRef(null);
    const { selectedFacetsToApply } = useSelectedFacetsToApply();
    const { customFacetFilters } = useUnifiedProductGridData();

    // Count pending filters from selectedFacetsToApply
    const pendingFilterCount = Object.values(selectedFacetsToApply).reduce(
      (acc, selectedFacetOptions) => {
        return acc + selectedFacetOptions?.length || 0;
      },
      0
    );

    // Count applied custom filters (only if not already in pending)
    const selectedFacetNames = new Set(Object.keys(selectedFacetsToApply));
    const customFilterCount = Object.values(customFacetFilters).filter(
      (item) => !item.hasError && !selectedFacetNames.has(item.name)
    ).length;

    const filterCount = pendingFilterCount + customFilterCount;

    // Restore focus to button when closing
    useEffect(() => {
      if (!isOpen && buttonRef.current) {
        buttonRef.current.focus();
      }
    }, [isOpen]);

    return (
      <div ref={ref}>
        <FilterButton
          ref={buttonRef}
          type="button"
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          onClick={() => setIsOpen((prev) => !prev)}
          data-autotag="filter-count"
        >
          <FormattedMessage
            id="site_product_grid.filter_label"
            defaultMessage="Filter"
          />
          {filterCount > 0 && (
            <FormattedMessage
              id="site_product_filter.filter_count"
              defaultMessage="({filterCount})"
              values={{
                filterCount: <FormattedNumber value={filterCount} />,
              }}
            />
          )}
          <FaChevronRight
            aria-hidden="true"
            style={{ fontSize: '12px', paddingTop: '4px' }}
          />
        </FilterButton>
      </div>
    );
  }
);

MobileGridFiltersButton.displayName = 'MobileGridFiltersButton';

MobileGridFiltersButton.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func,
};

export default MobileGridFiltersButton;
